import { inject as service } from "@ember/service"
import { tracked } from "@glimmer/tracking"
import type EmberStore from "cc-frontend/services/ember-store"
import type QueryService from "cc-frontend/services/query"
import { Resource } from "ember-could-get-used-to-this"
import { isNil } from "lodash-es"

interface QueryToResponse {
  standard_sets_used: {
    standardSetId: string
    groupIds: Array<string>
  }
  standards_used: {
    standardId: string
    groupId?: string
    userId?: string
    standardsUsed: string
  }
}

interface Args<T> {
  positional: [type: T, id: string | null | undefined, forceRefresh?: boolean]
}

export default class StaticQueryResource<
  QueryName extends keyof QueryToResponse,
  Type extends QueryToResponse[QueryName]
> extends Resource<Args<QueryName>["positional"]> {
  @service("ember-store") declare store: EmberStore
  @service declare query: QueryService

  declare type: keyof QueryToResponse | null
  declare id: string | null | undefined
  forceRefresh: boolean = false

  @tracked isPending = false
  @tracked isSettled = false
  @tracked isRejected = false
  @tracked isFulfilled = false
  @tracked results: Array<Type> | null = null

  setup() {
    if (this.args.positional) {
      this.type = this.args.positional[0]
      this.id = this.args.positional[1]
      this.forceRefresh = this.args.positional[2] || false
    }
    this._find()
  }

  _find() {
    if (isNil(this.type) || isNil(this.id)) return
    this.isPending = true
    this.isFulfilled = false
    this.isSettled = false
    let query = this.query.generateStaticQuery(this.type, this.id)

    this.query
      .fetch(query)
      .then((response: { data: { results: Array<Type> } }) => {
        this.isPending = false
        this.isFulfilled = true
        this.results = response.data?.results
      })
      .catch(() => {
        this.isRejected = true
      })
      .finally(() => {
        this.isSettled = true
      })
  }

  update() {
    if (this.args.positional) {
      let newType = this.args.positional[0]
      let newId = this.args.positional[1]
      if (newType !== this.type || newId !== this.id) {
        this.type = newType
        this.id = newId
        this._find()
      }
    }
  }

  teardown() {
    // we'll unsubscribe from it here.
  }
}
